import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { setFieldValue, setFieldDate } from "../../../redux/features/payrollField/slice";
import { addPayrollFieldValueThunk, fetchPayrollFieldsThunk } from "../../../redux/features/payrollField/payrollFieldThunks";
import { toast } from "react-toastify";

const PayrollSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const payrollFields = useSelector((state: RootState) => state.payrollField.fields);
  const fieldValues = useSelector((state: RootState) => state.payrollField.fieldValues);
  const savingField = useSelector((state: RootState) => state.payrollField.savingField);
  const loading = useSelector((state: RootState) => state.payrollField.loading);

  const [savingBatch, setSavingBatch] = useState(false);

  const changedKeys = useMemo(() => {
    const keys: string[] = [];
    (payrollFields || []).forEach((f: any) => {
      const current = fieldValues[f.field_code] || { field_value: "", effective_date: "" };
      if (current.field_value !== (f.field_value ?? "") || current.effective_date !== (f.effective_date ?? "")) {
        keys.push(f.field_code);
      }
    });
    return keys;
  }, [payrollFields, fieldValues]);

  const handleInputChange = (fieldCode: string, value: string) => {
    dispatch(setFieldValue({ fieldCode, value }));
  };

  const handleInputDateChange = (fieldCode: string, date: string | null) => {
    dispatch(setFieldDate({ fieldCode, date: date || "" }));
  };

  const handleSaveAll = async () => {
    if (!payrollFields || payrollFields.length === 0) return;
    const keys = changedKeys;
    if (keys.length === 0) return;
    setSavingBatch(true);
    let successCount = 0;
    const errors: string[] = [];
    for (const code of keys) {
      const fv = fieldValues[code] || { field_value: "", effective_date: "" };
      if (!fv.field_value) {
        errors.push(`${code}: missing value`);
        continue;
      }
      if (!fv.effective_date) {
        errors.push(`${code}: missing effective date`);
        continue;
      }

      try {
        // dispatch thunk per-field
        const resultAction: any = await dispatch(
          addPayrollFieldValueThunk({
            employee_id: (payrollFields[0] && payrollFields[0].employee_id) || "",
            field_code: code,
            field_value: fv.field_value,
            effective_date: fv.effective_date,
          })
        );

        if (resultAction && resultAction.payload && resultAction.payload.status) {
          successCount += 1;
        } else {
          errors.push(`${code}: ${resultAction.error?.message || 'failed'}`);
        }
      } catch (err: any) {
        errors.push(`${code}: ${err?.message || 'error'}`);
      }
    }

    if (successCount > 0) {
      toast.success("Payroll Field value saved successfully");
      // refresh list
      const employeeId = (payrollFields[0] && payrollFields[0].employee_id) || "";
      if (employeeId) dispatch(fetchPayrollFieldsThunk(employeeId));
    }
    if (errors.length > 0) {
      toast.error(`Some changes failed: ${errors.join('; ')}`);
    }
    setSavingBatch(false);
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-3">
        <div>
          <h3 className="text-black font-bold text-lg">Payroll Information</h3>
          <p className="text-sm text-gray-600 mt-2">Changes here affect payroll calculations based on the selected effective dates.</p>
        </div>
        <div className="mt-3 md:mt-0 md:ml-4 flex md:block justify-end">
          <button
            onClick={handleSaveAll}
            disabled={changedKeys.length === 0 || savingBatch}
            className="whitespace-nowrap bg-[#f0f2f5] hover:bg-[#e9ebee] border border-gray-300 text-blue-600 px-4 py-2 rounded disabled:opacity-50 text-sm shadow-sm"
          >
            {savingBatch ? (
              "Saving..."
            ) : (
              <>
                <span className="hidden md:inline">Save Changes</span>
                <span className="inline md:hidden">Save</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div>
        {payrollFields.map((field: any, idx: number) => (
          <div key={field.field_code} className={`py-6 px-0 ${idx < payrollFields.length - 1 ? 'border-b border-gray-100' : ''}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">{field.field_label}</label>
                {field.field_type === "DROPDOWN" ? (
                  <select
                    className="w-full border rounded p-3 text-sm h-12 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={fieldValues[field.field_code]?.field_value ?? ""}
                    onChange={(e) => handleInputChange(field.field_code, e.target.value)}
                  >
                    <option value="">Select {field.field_label}</option>
                    {field.field_options?.map((opt: any) => (
                      <option key={opt.code} value={opt.code}>{opt.value}</option>
                    ))}
                  </select>
                ) : field.field_type === "NUMBER" ? (
                  <input
                    className="w-full border rounded p-3 text-sm h-12 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    type="number"
                    value={fieldValues[field.field_code]?.field_value ?? ""}
                    onChange={(e) => handleInputChange(field.field_code, e.target.value)}
                  />
                ) : (
                  <input
                    className="w-full border rounded p-3 text-sm h-12 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={fieldValues[field.field_code]?.field_value ?? ""}
                    onChange={(e) => handleInputChange(field.field_code, e.target.value)}
                  />
                )}
              </div>

              <div>
                <label className="text-xs text-gray-600 mb-1 block">Effective from</label>
                <div className="relative">
                  <div className="flex items-center border rounded p-3 bg-white h-12 w-full">
                    <div className="text-sm text-gray-800 flex-1">
                      {fieldValues[field.field_code]?.effective_date
                        ? dayjs(fieldValues[field.field_code].effective_date).format("MMM D, YYYY")
                        : "MM/DD/YYYY"}
                    </div>
                    <div className="ml-2 p-1 text-gray-700">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="1.5" />
                        <path d="M16 2v4M8 2v4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                  <input
                    type="date"
                    className="absolute left-0 top-0 opacity-0 w-full h-12 z-40 cursor-pointer"
                    value={fieldValues[field.field_code]?.effective_date ? dayjs(fieldValues[field.field_code].effective_date).isValid() ? dayjs(fieldValues[field.field_code].effective_date).format("YYYY-MM-DD") : "" : ""}
                    onChange={(e) => handleInputDateChange(field.field_code, e.target.value)}
                    onClick={(e) => { const input = e.target as HTMLInputElement; try { (input as any).showPicker?.(); } catch (err) { /* ignore */ } }}
                    onFocus={(e) => { const input = e.target as HTMLInputElement; try { (input as any).showPicker?.(); } catch (err) { /* ignore */ } }}
                    aria-label={`Select effective date for ${field.field_label}`}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* optional last-updated footer if metadata present */}
      {payrollFields && payrollFields.length > 0 && (payrollFields[0].updated_at || payrollFields[0].updated_by) && (
        <div className="mt-4 text-xs text-gray-600 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 8v4l3 3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="9" strokeWidth="1.5" />
          </svg>
          <div>
            {payrollFields[0].updated_at ? (
              <span>pk:t updated: {dayjs(payrollFields[0].updated_at).format('MMM D, YYYY')}</span>
            ) : null}
            {payrollFields[0].updated_by ? <span className="ml-2">by {payrollFields[0].updated_by}</span> : null}
          </div>
        </div>
      )}

      <div className="fixed bottom-4 right-4 md:hidden z-50">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 p-3 rounded-full shadow-lg disabled:opacity-50 transition"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      </div>
    </div>
  );
};

export default PayrollSection;